<template>
	<component :is="component" :story="story">
		<nuxt-error-boundary
			v-for="(blok, index) in body"
			:key="blok.uuid || blok._uid"
			@error="handleError"
		>
			<nuxt-client-fallback @ssr-error="handleError">
				<!-- Only import the few first components eagerly (enough
                components that they should be enough to cover the initial
                viewport) -->
				<v-storyblok-component
					v-if="index <= 4"
					:blok="blok"
					:component-index="index"
				/>

				<!-- Delay the hydration of later components. This has a major
                improvement on performance since it makes nuxt split the JS
                code of the components that aren't initially visible and it
                also delays the hydration ("making interactive") of the
                components so that the page rendering has more bandwidth to
                finish earlier. -->

				<!-- Components that are earlier in the page are hydrated in a
                delayed manner so that they don't block other page processing,
                but still as soon as there's free time (when the browser has
                finished processing the main page render) so that they don't
                flash into interactive if the user scrolls down quickly to
                them. We also set a max timeout when the components should be
                made interactive at the latest, if not earlier. -->
				<LazyIdleVStoryblokComponent
					v-else-if="index <= 8"
					:hydrate-on-idle="3000"
					:blok="blok"
					:component-index="index"
				/>

				<!-- Components that are lower in the page (and probably aren't
                ever browsed to by most users) are only hydrated if they ever
                come into the viewport. This keeps the earlier page components
                reasonably snappy and saves bandwidth if the page content gets
                long. -->
				<LazyVisibleVStoryblokComponent
					v-else
					:hydrate-on-visible="{rootMargin: '500px'}"
					:blok="blok"
					:component-index="index"
				/>

				<template #fallback>
					<div class="hidden">
						[Error encountered with Storyblok component: {{ blok.component }}]
					</div>
				</template>
			</nuxt-client-fallback>

			<template #error>
				<div class="hidden">
					[Error encountered with Storyblok component: {{ blok.component }}]
				</div>
			</template>
		</nuxt-error-boundary>
	</component>
</template>

<script setup lang="ts">
import {captureException} from "@sentry/vue";
import type {ISbStoryData} from "storyblok-js-client";

const properties = defineProps<{
	story?: ISbStoryData;
}>();

type StoryblokBodyBlok = {
	uuid?: string;
	_uid?: string;
	component?: string;
};

const component = computed<unknown>(
	() => properties.story?.content?.component || "div",
);
const body = computed<StoryblokBodyBlok[]>(() =>
	Array.isArray(properties.story?.content?.body)
		? (properties.story.content.body as StoryblokBodyBlok[])
		: [],
);

const handleError = (error: unknown) => {
	// eslint-disable-next-line no-console
	console.error(error);
	captureException(error);
};
</script>
